import {
  composeValidators,
  requiredText,
  runFieldValidation,
  validateEmail,
  validateItems,
  validateRequired,
  type FieldErrors,
  type ItemsErrors,
} from "./validation";
import {
  toEnKey,
  type HomeFieldConfig,
  type HomeFieldValue,
  type HomeSectionConfig,
  type HomeSectionData,
  type HomeSectionItem,
  type HomeSectionValues,
} from "../type/homePage";

/** Batas ukuran satu file gambar (MB). Ubah di sini kalau batas backend berbeda. */
export const MAX_IMAGE_SIZE_MB = 5;

let uidCounter = 0;

/** Id lokal untuk React key. Sengaja tidak pakai crypto.randomUUID (butuh HTTPS/localhost). */
export const nextUid = () => `home-item-${++uidCounter}`;

const emptyValues = (fields: HomeFieldConfig[]): HomeSectionValues => {
  const values: HomeSectionValues = {};
  fields.forEach((field) => {
    values[field.key] = field.type === "image" ? null : "";
    if (field.translatable) values[toEnKey(field.key)] = "";
  });
  return values;
};

const toFieldValue = (field: HomeFieldConfig, raw: unknown): HomeFieldValue => {
  if (field.type === "image") {
    return typeof raw === "string" && raw !== "" ? raw : null;
  }
  return raw === null || raw === undefined ? "" : String(raw);
};

const parseValues = (
  fields: HomeFieldConfig[],
  raw: Record<string, unknown> | null | undefined
): HomeSectionValues => {
  const values: HomeSectionValues = {};
  fields.forEach((field) => {
    values[field.key] = toFieldValue(field, raw?.[field.key]);
    if (field.translatable) {
      const enKey = toEnKey(field.key);
      values[enKey] = toFieldValue(field, raw?.[enKey]);
    }
  });
  return values;
};

export const createEmptyItem = (
  config: HomeSectionConfig
): HomeSectionItem => ({
  uid: nextUid(),
  values: emptyValues(config.items?.fields ?? []),
});

/** Mengubah response backend menjadi state form. `data` boleh kosong (section belum pernah disimpan). */
export const parseSectionData = (
  config: HomeSectionConfig,
  data: HomeSectionData | null | undefined
): { values: HomeSectionValues; items: HomeSectionItem[] } => {
  const values = parseValues(config.fields, data);

  const items: HomeSectionItem[] =
    config.items && Array.isArray(data?.items)
      ? data!.items!.map((raw) => ({
        uid: nextUid(),
        id: typeof raw.id === "number" ? raw.id : undefined,
        values: parseValues(config.items!.fields, raw),
      }))
      : [];

  return { values, items };
};

const cleanText = (value: HomeFieldValue): string =>
  typeof value === "string" ? value.trim() : "";

/** Membangun FormData sesuai kontrak di src/api/homePageApi.ts. */
export const buildSectionFormData = (
  config: HomeSectionConfig,
  values: HomeSectionValues,
  items: HomeSectionItem[]
): FormData => {
  const formData = new FormData();

  config.fields.forEach((field) => {
    const value = values[field.key];

    if (field.type === "image") {
      if (value instanceof File) {
        formData.append(field.key, value);
      } else if (value === null) {
        formData.append(`remove_${field.key}`, "true");
      }
      // string = URL lama, tidak diubah -> tidak perlu dikirim.
      return;
    }

    formData.append(field.key, cleanText(value));

    if (field.translatable) {
      const enKey = toEnKey(field.key);
      formData.append(enKey, cleanText(values[enKey]));
    }
  });

  if (config.items) {
    const itemFields = config.items.fields;

    const payload = items.map((item, index) => {
      const row: Record<string, unknown> = {
        id: item.id ?? null,
        sort_order: index + 1,
      };

      itemFields.forEach((field) => {
        const value = item.values[field.key];

        if (field.type === "image") {
          if (value instanceof File) {
            formData.append(`items[${index}][${field.key}]`, value);
            row[field.key] = null;
          } else {
            row[field.key] = value; // URL lama atau null
          }
          return;
        }

        row[field.key] = cleanText(value);

        if (field.translatable) {
          const enKey = toEnKey(field.key);
          row[enKey] = cleanText(item.values[enKey]);
        }
      });

      return row;
    });

    formData.append("items", JSON.stringify(payload));
  }

  return formData;
};

export interface SectionValidationResult {
  fieldErrors: FieldErrors;
  itemsErrors: ItemsErrors;
  itemsGeneralError?: string;
  hasError: boolean;
}

const buildRule = (field: HomeFieldConfig) => {
  const rules: Array<(value: HomeFieldValue) => string | undefined> = [];

  if (field.required) {
    rules.push((value) =>
      validateRequired(value, `${field.label} is required`)
    );
  }

  if (field.type === "email") {
    rules.push((value) => validateEmail(cleanText(value)));
  }

  return rules.length > 0 ? composeValidators(...rules) : undefined;
};

export const validateSection = (
  config: HomeSectionConfig,
  values: HomeSectionValues,
  items: HomeSectionItem[]
): SectionValidationResult => {
  // Field tingkat section
  const validators: Record<string, (value: HomeFieldValue) => string | undefined> =
    {};
  config.fields.forEach((field) => {
    const rule = buildRule(field);
    if (rule) validators[field.key] = rule;
  });

  // Nilai teks yang hanya berisi spasi dianggap kosong.
  const trimmedValues: HomeSectionValues = {};
  config.fields.forEach((field) => {
    const value = values[field.key];
    trimmedValues[field.key] =
      typeof value === "string" ? value.trim() : value;
  });

  const fieldErrors = runFieldValidation(trimmedValues, validators);

  // Item berulang
  let itemsErrors: ItemsErrors = {};
  let itemsGeneralError: string | undefined;

  if (config.items) {
    const { min, itemLabel, fields } = config.items;

    if (min && items.length < min) {
      itemsGeneralError = `At least ${min} ${itemLabel} is required`;
    }

    if (items.length > 0) {
      const rules: Record<
        string,
        (value: HomeFieldValue) => string | undefined
      > = {};

      fields.forEach((field) => {
        if (field.required) {
          rules[field.key] = (value) =>
            requiredText(`${field.label} is required`)(
              typeof value === "string" ? value.trim() : value
            );
        } else if (field.type === "email") {
          rules[field.key] = (value) => validateEmail(cleanText(value));
        }
      });

      itemsErrors = validateItems(
        items.map((item) => item.values),
        rules
      ).itemsErrors;
    }
  }

  return {
    fieldErrors,
    itemsErrors,
    itemsGeneralError,
    hasError:
      Object.keys(fieldErrors).length > 0 ||
      Object.keys(itemsErrors).length > 0 ||
      Boolean(itemsGeneralError),
  };
};